/* eslint-disable @reatom/unit-naming-rule */
import {
  action,
  isAbortError,
  onDisconnect,
  reaction,
  withAssign,
} from '@reatom/framework'

import { AsyncStateN, Equivalence, type Result } from '@blade/std-lib'
import { Error } from '@root/shared/core'

import { reatomAsyncState } from './reatom-async-state'

import type { CtxSpy, ReactionAtom } from '@reatom/framework'

export const reatomAsyncResult = <
  E,
  A,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Params extends Array<any> = [],
>(
  fn: (
    ctx: CtxSpy,
    ...params: Params
  ) => Promise<Result.Result<A, E | Error.UnknownError>>,
  args?: {
    name?: string
    eq?: Equivalence.Equivalence<A>
  },
) => {
  const { name = 'unknown', eq = Equivalence.strictEqual() } = args ?? {}

  const stateAtom = reatomAsyncState(
    AsyncStateN.notInitiated<E | Error.UnknownError, A>(),
    {
      name,
      eq,
    },
  )

  let currentReaction: ReactionAtom<unknown> | undefined

  const runReaction = reaction(async (ctx, ...params: Params) => {
    const currentState = ctx.get(stateAtom)
    stateAtom.setLoading(ctx)

    let result
    try {
      // https://t.me/reatom_ru/100819
      // eslint-disable-next-line @reatom/async-rule
      result = await fn(ctx, ...params)
    } catch (error) {
      if (isAbortError(error)) {
        stateAtom.set(ctx, currentState)
        return
      }
      stateAtom.setError(ctx, Error.UnknownError.fromUnknown(error) as E)
      return
    }
    stateAtom.setResult(ctx, result)
  }, `async-result.${name}.runReaction`)

  const runAction = action(async (ctx, ...params: Params) => {
    currentReaction?.unsubscribe()
    currentReaction = runReaction(ctx, ...params)
  }, `async-result.${name}.runAction`)

  onDisconnect(stateAtom, () => {
    currentReaction?.unsubscribe()
  })

  return stateAtom.pipe(
    withAssign(() => ({
      run: runAction,
      /**
       * Aborts fetch reaction
       */
      stop: () => currentReaction?.unsubscribe(),
    })),
  )
}
